import {
  BaseEvent,
  EventCardsSliderNavigation,
  EventCommunicationError,
  EventFormInteractRequest,
  EventHeroCarousel,
  EventNavigationMenu,
  EventOrderListRowAction,
  EventProductInCart,
  PageLoadEvent,
} from '../models';
import {
  communicationError,
  dynamicLinkClick,
  dynamicLinkClickWithPosition,
  externalLinkClick,
  formInteractionAddToCart,
  formInteractionWithPosition,
  hover,
  modalEvent,
  orderListRowInteraction,
  pageLoad,
} from './chase-analytics-mapper.utils';
import { addMerchantQueryParam } from './merchant-query-param.util';

export const eventRecognition: Record<keyof typeof mappers, (event: any) => boolean> = {
  productInCart: (event: BaseEvent) =>
    'brand' in event &&
    'productName' in event &&
    'productDetails' in event &&
    event.eventType === 'click' &&
    !!event.isFormInteraction,
  navigationMenu: (event: BaseEvent) => 'position' in event && 'linkName' in event && event.eventType === 'click',
  formInteraction: (event: BaseEvent) => event.eventType === 'formInteraction' || !!event.isFormInteraction,
  heroCarousel: (event: BaseEvent) => 'position' in event && !('direction' in event) && event.eventType === 'click',
  cardsSliderNavigation: (event: BaseEvent) =>
    'position' in event && 'direction' in event && event.eventType === 'click',
  orderListRowAction: (event: BaseEvent) =>
    'order' in event &&
    'item' in event &&
    (event.item === 'collapse' || event.item === 'expand') &&
    event.eventType === 'click',
  pageLoadAction: (event: BaseEvent) => event.eventType === 'pageLoad',
  hoverAction: (event: BaseEvent) => event.eventType === 'hover',
  externalLinkClickAction: (event: BaseEvent) => !!event.isExternal,
  modalAction: (event: BaseEvent) => event.eventType === 'modal',
  communicationAction: (event: BaseEvent) => event.eventType === 'communication',
  baseClickAction: (event: BaseEvent) => event.eventType === 'click',
};

export const mappers = {
  pageLoadAction: (event: PageLoadEvent): Record<string, any> => {
    // addMerchantQueryParam(event);
    return pageLoad(event);
  },
  productInCart: (event: EventProductInCart): Record<string, any> => formInteractionAddToCart(event),
  navigationMenu: (event: EventNavigationMenu): Record<string, any> => dynamicLinkClickWithPosition(event),
  formInteraction: (event: EventFormInteractRequest): Record<string, any> => formInteractionWithPosition(event),
  heroCarousel: (event: EventHeroCarousel): Record<string, any> => dynamicLinkClickWithPosition(event),
  cardsSliderNavigation: (event: EventCardsSliderNavigation): Record<string, any> =>
    dynamicLinkClickWithPosition(event),
  orderListRowAction: (event: EventOrderListRowAction): Record<string, any> => orderListRowInteraction(event),
  hoverAction: (event: BaseEvent): Record<string, any> => hover(event),
  baseClickAction: (event: BaseEvent): Record<string, any> => dynamicLinkClick(event),
  modalAction: (event: BaseEvent): Record<string, any> => modalEvent(event),
  externalLinkClickAction: (event: BaseEvent): Record<string, any> => externalLinkClick(event),
  communicationAction: (event: EventCommunicationError): Record<string, any> => communicationError(event),
} as const;

const eventTypeMappings = [
  {
    identifier: eventRecognition.productInCart,
    mapFunction: (event: BaseEvent) => mappers.productInCart(event as EventProductInCart),
  },
  {
    identifier: eventRecognition.navigationMenu,
    mapFunction: (event: BaseEvent) => mappers.navigationMenu(event as EventNavigationMenu),
  },
  {
    identifier: eventRecognition.formInteraction,
    mapFunction: (event: BaseEvent) => mappers.formInteraction(event as EventFormInteractRequest),
  },
  {
    identifier: eventRecognition.heroCarousel,
    mapFunction: (event: BaseEvent) => mappers.heroCarousel(event as EventHeroCarousel),
  },
  {
    identifier: eventRecognition.cardsSliderNavigation,
    mapFunction: (event: BaseEvent) => mappers.cardsSliderNavigation(event as EventCardsSliderNavigation),
  },
  {
    identifier: eventRecognition.orderListRowAction,
    mapFunction: (event: BaseEvent) => mappers.orderListRowAction(event as EventOrderListRowAction),
  },
  {
    identifier: eventRecognition.pageLoadAction,
    mapFunction: (event: BaseEvent) => mappers.pageLoadAction(event as BaseEvent),
  },
  {
    identifier: eventRecognition.hoverAction,
    mapFunction: (event: BaseEvent) => mappers.hoverAction(event as BaseEvent),
  },
  {
    identifier: eventRecognition.modalAction,
    mapFunction: (event: BaseEvent) => mappers.modalAction(event as BaseEvent),
  },
  {
    identifier: eventRecognition.communicationAction,
    mapFunction: (event: BaseEvent) => mappers.communicationAction(event as unknown as EventCommunicationError),
  },
  {
    identifier: eventRecognition.baseClickAction,
    mapFunction: (event: BaseEvent) => mappers.baseClickAction(event as BaseEvent),
  },
];

export function identifyAndMapEvent(event: BaseEvent): Record<string, any> {
  for (const mapping of eventTypeMappings) {
    if (mapping.identifier(event)) {
      return mapping.mapFunction(event);
    }
  }
  console.warn('Unknown event type');
  console.warn(event);
  return {};
}
